import React, { useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Box, Stack } from '@mui/material'
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@material-ui/icons'
import { parse } from 'papaparse'
import { useDispatch } from 'react-redux'
import { bulkInsertBuildings } from 'store/building'
import ProgressButton from 'components/ProgressButton'
import { downloadBuildingSampleCSV } from 'utils/downloadCSV'
import { parseCsvData } from 'utils'
import { useStyles } from 'views/admin/BadgesV1/ImportBadgesViaCSV/styles'
import { toast } from 'utils/notify'

const ImportBuildingCSVModal = ({ open, onClose, conferenceId, buildings }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const csvInputRef = useRef()

  const [showInstructions, setShowInstructions] = useState(true)
  const [csvFile, setCsvFile] = useState(null)
  const [csvError, setCsvError] = useState('')
  const [csvParsedRows, setCsvParsedRows] = useState([])
  const [invalidCount, setInvalidCount] = useState(0)
  const [duplicateCount, setDuplicateCount] = useState(0)
  const [csvDuplicateCount, setCsvDuplicateCount] = useState(0)
  const [showCsvReview, setShowCsvReview] = useState(false)
  const [importing, setImporting] = useState(false)

  const getRowErrors = (row) => {
    const errors = new Set()
    if (!row.title) errors.add('title')
    return errors
  }

  const norm = (v) => (v || '').trim().toLowerCase()

  const rowKey = (r) => `${norm(r.title)}|${norm(r.floor)}|${norm(r.room)}`

  const existingBuildingKey = (b) => `${norm(b.title)}|${norm(b.floorNum)}|${norm(b.roomNum)}`

  const countDuplicates = (rows) => {
    const existingKeys = new Set((buildings || []).map(existingBuildingKey))
    return rows.filter((r) => existingKeys.has(rowKey(r))).length
  }

  // Returns indices of rows that are duplicated within the CSV itself (all occurrences after the first)
  const getCsvDuplicateIndices = (rows) => {
    const seen = new Map()
    const dupeIndices = new Set()
    rows.forEach((r, i) => {
      const key = rowKey(r)
      if (seen.has(key)) {
        dupeIndices.add(i)
        dupeIndices.add(seen.get(key))
      } else {
        seen.set(key, i)
      }
    })
    return dupeIndices
  }

  const resetState = () => {
    setCsvFile(null)
    setCsvError('')
    setCsvParsedRows([])
    setInvalidCount(0)
    setDuplicateCount(0)
    setCsvDuplicateCount(0)
    setShowCsvReview(false)
    if (csvInputRef.current) csvInputRef.current.value = null
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setCsvError('Invalid file type. Please upload a .csv file.')
      setCsvParsedRows([])
      setInvalidCount(0)
      setDuplicateCount(0)
      setCsvDuplicateCount(0)
      setShowCsvReview(false)
      event.target.value = null
      return
    }

    setCsvFile(file)
    setCsvError('')
    setCsvParsedRows([])
    setInvalidCount(0)
    setDuplicateCount(0)
    setCsvDuplicateCount(0)
    setShowCsvReview(false)

    parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        const rows = parseCsvData(results.data)

        if (!rows.length) {
          setCsvError('No rows found in the CSV file.')
          return
        }

        const validRows = rows.filter((r) => getRowErrors(r).size === 0)
        const badCount = rows.length - validRows.length
        const dupCount = countDuplicates(validRows)
        const csvDupIndices = getCsvDuplicateIndices(validRows)

        setInvalidCount(badCount)
        setDuplicateCount(dupCount)
        setCsvDuplicateCount(csvDupIndices.size)
        setCsvParsedRows(rows)
        setShowCsvReview(true)
      },
      error: (err) => {
        setCsvError(err.message || 'Failed to parse CSV file.')
      }
    })

    event.target.value = null
  }

  const handleImport = async () => {
    if (!csvParsedRows.length || !showCsvReview) {
      setCsvError('Please select and review a CSV file before importing.')
      return
    }

    const existingKeys = new Set((buildings || []).map(existingBuildingKey))

    const seenKeys = new Set()
    const validRows = csvParsedRows
      .filter((r) => getRowErrors(r).size === 0)
      .filter((r) => {
        const key = rowKey(r)
        if (seenKeys.has(key)) return false
        seenKeys.add(key)
        return !existingKeys.has(key)
      })
      .map(({ title, floor, room }) => ({
        title,
        floorNum: floor || '',
        roomNum: room || '',
        conferenceId
      }))

    if (!validRows.length) {
      toast.error('No valid rows found to import.')
      setCsvError('No new valid rows to import.')
      return
    }

    setImporting(true)
    try {
      await dispatch(bulkInsertBuildings(validRows))
      handleClose()
      toast.success(`${validRows.length} Buildings are imported successfully.`)
    } catch (err) {
      console.error('error', err)
      // toast.error already shown inside bulkInsertBuildings
    } finally {
      setImporting(false)
    }
  }

  const importDisabled = importing || !showCsvReview

  return (
    <Dialog open={open} maxWidth="xl" fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.dialogTitle}>
        <Typography variant="h5">Import Buildings from CSV</Typography>
        {!importing && (
          <IconButton onClick={handleClose} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <DialogContent className={classes.dialogContent}>
        {/* Instructions panel */}
        <Box className={classes.note}>
          <Box className={classes.noteHeader} onClick={() => setShowInstructions((v) => !v)}>
            <Typography className={classes.noteHead}>Instructions to Import Buildings:</Typography>
            {showInstructions ? (
              <ExpandLessIcon className={classes.accordionIcon} />
            ) : (
              <ExpandMoreIcon className={classes.accordionIcon} />
            )}
          </Box>

          {showInstructions && (
            <>
              <Box className={classes.noteSteps}>
                <Box className={classes.stepItem}>
                  <Box className={classes.stepNumber}>1</Box>
                  <Typography className={classes.stepText}>
                    Prepare a CSV file with the columns listed below, then select it from your
                    computer.
                  </Typography>
                </Box>
                <Box className={classes.stepItem}>
                  <Box className={classes.stepNumber}>2</Box>
                  <Typography className={classes.stepText}>
                    {`Review the data and click 'Import Buildings' to bulk-add all buildings.`}
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.fileFormatInfo}>
                <Box>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1rem'
                    }}>
                    <Typography variant="subtitle2" className={classes.formatTitle}>
                      Sample CSV Format
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      className={classes.sampleDownloadButton}
                      onClick={downloadBuildingSampleCSV}
                      startIcon={<DownloadIcon />}>
                      Download Sample CSV
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.formatHeaderCell}>
                            Title <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>Floor</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Room</TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* File upload */}
        <Box mt={3}>
          <input
            type="file"
            accept=".csv"
            ref={csvInputRef}
            className={classes.input}
            onChange={handleFileChange}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              component="span"
              className={classes.upload}
              startIcon={<CloudUploadIcon />}
              onClick={() => csvInputRef.current && csvInputRef.current.click()}>
              {csvFile ? csvFile.name : 'Select CSV file'}
            </Button>
            {csvFile && (
              <IconButton
                size="small"
                title="Clear selected file"
                onClick={() => {
                  setCsvFile(null)
                  setCsvError('')
                  setCsvParsedRows([])
                  setInvalidCount(0)
                  setDuplicateCount(0)
                  setCsvDuplicateCount(0)
                  setShowCsvReview(false)
                  if (csvInputRef.current) csvInputRef.current.value = null
                }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
          {csvError && <Typography className={classes.error}>{csvError}</Typography>}
        </Box>

        {/* Review table */}
        {showCsvReview && csvParsedRows.length > 0 && (
          <Box className={classes.tableContainer}>
            <Typography className={classes.tableTitle}>
              Review Buildings ({csvParsedRows.length})
            </Typography>

            {invalidCount > 0 && (
              <Box className={classes.warningBanner}>
                <InfoIcon className={classes.warningIcon} />
                <Typography className={classes.warningText}>
                  <strong>
                    {invalidCount} row{invalidCount !== 1 ? 's are' : ' is'} missing the required
                    Title field
                  </strong>{' '}
                  (highlighted in red below). These rows will be <strong>skipped</strong> during
                  import.
                </Typography>
              </Box>
            )}

            {csvDuplicateCount > 0 && (
              <Box
                className={classes.warningBanner}
                style={{ backgroundColor: '#fffde7', borderColor: '#f9a825' }}>
                <InfoIcon className={classes.warningIcon} style={{ color: '#f9a825' }} />
                <Typography className={classes.warningText}>
                  <strong>
                    {csvDuplicateCount} row{csvDuplicateCount !== 1 ? 's' : ''} within this CSV
                    share identical data
                  </strong>{' '}
                  (highlighted in orange below). Only the first occurrence will be imported; the
                  rest will be <strong>skipped</strong>.
                </Typography>
              </Box>
            )}

            {duplicateCount > 0 && (
              <Box className={classes.warningBanner}>
                <InfoIcon className={classes.warningIcon} />
                <Typography className={classes.warningText}>
                  <strong>
                    {duplicateCount} row{duplicateCount !== 1 ? 's' : ''} already exist
                  </strong>{' '}
                  in the system and will be <strong>skipped</strong> to avoid duplicates.
                </Typography>
              </Box>
            )}

            {invalidCount === 0 && duplicateCount === 0 && csvDuplicateCount === 0 && (
              <Box className={classes.importInfo}>
                <Box className={classes.infoNumberCircle}>i</Box>
                <Typography className={classes.importInfoText}>
                  All {csvParsedRows.length} row{csvParsedRows.length !== 1 ? 's' : ''} look good.
                  Click <strong>Import Buildings</strong> to proceed.
                </Typography>
              </Box>
            )}

            <TableContainer component={Paper} className={classes.reviewTable}>
              <Table size="small">
                <TableHead className={classes.reviewHeader}>
                  <TableRow>
                    <TableCell className={classes.reviewHeaderCell}>
                      Title <span className={classes.requiredMark}>*</span>
                    </TableCell>
                    <TableCell className={classes.reviewHeaderCell}>Floor</TableCell>
                    <TableCell className={classes.reviewHeaderCell}>Room</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const csvDupeIndices = getCsvDuplicateIndices(csvParsedRows)
                    return csvParsedRows.map((row, index) => {
                      const errors = getRowErrors(row)
                      const rowInvalid = errors.size > 0
                      const rowCsvDupe = !rowInvalid && csvDupeIndices.has(index)
                      const borderColor = rowInvalid ? '#f44336' : rowCsvDupe ? '#ff9800' : null
                      return (
                        <TableRow
                          key={index}
                          style={borderColor ? { borderLeft: `3px solid ${borderColor}` } : {}}>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              errors.has('title')
                                ? classes.cellError
                                : rowCsvDupe
                                ? classes.cellConflict
                                : ''
                            }`}>
                            {row.title || <span className={classes.missingValue}>Missing</span>}
                          </TableCell>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              rowCsvDupe ? classes.cellConflict : ''
                            }`}>
                            {row.floor || '-'}
                          </TableCell>
                          <TableCell
                            className={`${classes.reviewCell} ${
                              rowCsvDupe ? classes.cellConflict : ''
                            }`}>
                            {row.room || '-'}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <Box className={classes.buttonBox}>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <ProgressButton
          label="Import Buildings"
          onClick={handleImport}
          disabled={importDisabled}
          inProgress={importing}
          variant="contained"
          color="primary"
        />
      </Box>
    </Dialog>
  )
}

export default ImportBuildingCSVModal
